import addHomeLocation from "@assets/images/add-home-location.svg";
import { Button } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import CardActions from "@material-ui/core/CardActions";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import CardTitle from "@saleor/components/CardTitle";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { decimal } from "@saleor/misc";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import {
  useHomeLocationCreate,
  useHomeLocationDelete,
  useHomeLocationUpdate
} from "@saleor/warehouses/mutations";
import React, { useState } from "react";
import SVG from "react-inlinesvg";
import { FormattedMessage, useIntl } from "react-intl";

import { warehouseUrl } from "../../urls";
import constants from "./constants";
import HomeLocation from "./HomeLocation";
import HomeLocationActionDialog from "./HomeLocationActionDialog";
import HomeLocationDeleteDialog from "./HomeLocationDeleteDialog";

const useStyles = makeStyles(
  theme => ({
    button: {
      "& span": {
        color: "#002972"
      },
      color: "#002972",
      marginRight: theme.spacing(1)
    },
    card: {
      maxHeight: "818px",
      overflowY: "auto"
    },
    errorSpiel: {
      margin: "20px"
    }
  }),
  { name: "HomeLocationSection" }
);

function HomeLocationSection(props) {
  const intl = useIntl();
  const classes = useStyles(props);
  const { homeLocations, id, params, refetch } = props;
  const { HOME_LOCATION_SPIELS } = constants;
  const noHomeLocations = !homeLocations || homeLocations.length === 0;
  const [state, setState] = useState({
    data: null,
    isOpen: false,
    mode: ""
  });
  const notify = useNotifier();
  const navigate = useNavigator();

  const [updateHomeLocation] = useHomeLocationUpdate({
    onCompleted: data => {
      if (data.updateHomeLocation.errors.length === 0) {
        notify({
          status: "success",
          text: `You have successfully saved ${data.updateHomeLocation.homeLocation.name}`
        });
      }
    }
  });

  const [deleteHomeLocation] = useHomeLocationDelete({
    onCompleted: data => {
      if (data.deleteHomeLocation.errors.length === 0) {
        notify({
          status: "success",
          text: `You have successfully saved ${data.deleteHomeLocation.homeLocation.name}`
        });
      }
    }
  });

  const [createHomeLocation] = useHomeLocationCreate({
    onCompleted: data => {
      const { createHomeLocation } = data;
      if (createHomeLocation.errors.length === 0) {
        notify({
          status: "success",
          text: `You have successfully saved ${createHomeLocation.homeLocation.name}`
        });
      } else {
        notify({
          status: "error",
          text: "Some Fields have null value!"
        });
      }
    }
  });

  const [openModal, closeModal] = createDialogActionHandlers(
    navigate,
    params => warehouseUrl(id, params),
    params
  );

  function handleAdd() {
    openModal("add-location");
    setState({
      ...state,
      data: null,
      mode: "Add"
    });
  }

  function handleEdit(data) {
    openModal("edit-location");
    setState({
      ...state,
      data,
      mode: "Edit"
    });
  }

  function handleConfirm(data) {
    if (state.mode === "Add") {
      createHomeLocation({
        variables: {
          homeLocationName: data.name,
          latitude: decimal(data.latitude),
          longitude: decimal(data.longitude),
          remarks: data.remarks,
          warehouseId: id
        }
      });
      refetch();
    } else if (state.mode === "Edit") {
      updateHomeLocation({
        variables: {
          homeLocationId: data.id,
          homeLocationName: data.name,
          latitude: decimal(data.latitude),
          longitude: decimal(data.longitude),
          remarks: data.remarks
        }
      });
      refetch();
    }
    closeModal();
  }

  function handleDelete(data) {
    openModal("delete-location");
    setState({
      ...state,
      data,
      mode: "Delete"
    });
  }

  return (
    <div>
      <Card className={classes.card}>
        <CardTitle
          title={intl.formatMessage({
            defaultMessage: "Home Location"
          })}
        />

        {noHomeLocations ? (
          <Typography color="textSecondary" className={classes.errorSpiel}>
            <FormattedMessage defaultMessage="This warehouse has no Home Location assigned." />
          </Typography>
        ) : (
          homeLocations?.map(homeLocation => (
            <HomeLocation
              key={homeLocation?.id}
              {...homeLocation}
              onDelete={() => handleDelete(homeLocation)}
              onEdit={() => handleEdit(homeLocation)}
            />
          ))
        )}

        <CardActions>
          <Button className={classes.button} onClick={handleAdd}>
            {HOME_LOCATION_SPIELS.ADD_HOME_LOCATION}{" "}
            <SVG src={addHomeLocation} />
          </Button>
        </CardActions>
      </Card>

      <HomeLocationActionDialog
        open={
          params.action === "edit-location" || params.action === "add-location"
        }
        data={state?.data}
        mode={state.mode}
        onClose={closeModal}
        onConfirm={handleConfirm}
      />

      <HomeLocationDeleteDialog
        data={state.data}
        open={params.action === "delete-location"}
        onClose={closeModal}
        title={"Delete Home Location"}
        confirmButtonLabel={"YES"}
        cancelButtonLabel={"CANCEL"}
        onCancel={closeModal}
        onConfirm={() => {
          deleteHomeLocation({
            variables: {
              homeLocationId: state.data.id
            }
          });
          closeModal();
          refetch();
        }}
      />
    </div>
  );
}

export default HomeLocationSection;
